
import React, { useState, useRef } from 'react';
import { FileText, Copy, Download, Sparkles, Wand2, BookOpen, CheckCircle, FileCode, FileSpreadsheet, FileCheck } from 'lucide-react';
import { TEMPLATES, mapContentToSections, fillPlaceholders, exportAsMarkdown, exportAsHTML, type DeliverableTemplate } from './lib/engine';
import { aiPolishSection } from './lib/ai-fallback';
import { SettingsButton } from './lib/SettingsButton';

interface SavedDeliverable { id: string; name: string; template: string; values: Record<string, string>; polishedSections: Record<string, string>; date: string; }

export default function App() {
  const [selectedTemplate, setSelectedTemplate] = useState<DeliverableTemplate | null>(null);
  const [rawContent, setRawContent] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [activeSection, setActiveSection] = useState(0);
  const [polishedSections, setPolishedSections] = useState<Record<string, string>>({});
  const [isPolishing, setIsPolishing] = useState<string | null>(null);
  const [saved, setSaved] = useState<SavedDeliverable[]>(() => JSON.parse(localStorage.getItem('ca_deliverables') || '[]'));
  const [previewMode, setPreviewMode] = useState<'edit' | 'preview'>('edit');
  const streamRef = useRef('');

  const handleTemplateSelect = (t: DeliverableTemplate) => {
    setSelectedTemplate(t);
    setValues({});
    setPolishedSections({});
    setActiveSection(0);
    setRawContent('');
  };

  const handleAutoMap = () => {
    if (!selectedTemplate || !rawContent.trim()) return;
    const mapped = mapContentToSections(rawContent, selectedTemplate);
    const newValues: Record<string, string> = {};
    mapped.sections.forEach(s => {
      newValues[sectionKey(s.title)] = s.content;
    });
    setValues(prev => ({ ...prev, ...newValues }));
  };

  const handlePolish = async (sectionIdx: number) => {
    if (!selectedTemplate) return;
    const section = selectedTemplate.sections[sectionIdx];
    const key = section.title;
    setIsPolishing(key);
    streamRef.current = '';
    setPolishedSections(prev => ({ ...prev, [key]: '' }));
    await aiPolishSection(section.title, values[sectionKey(section.title)] || section.content, selectedTemplate.name, (chunk) => {
      streamRef.current += chunk;
      setPolishedSections(prev => ({ ...prev, [key]: streamRef.current }));
    });
    setIsPolishing(null);
  };

  const handleSave = () => {
    if (!selectedTemplate) return;
    const item: SavedDeliverable = {
      id: Date.now().toString(),
      name: values.PROJECT_NAME || values.CLIENT || selectedTemplate.name,
      template: selectedTemplate.id,
      values: { ...values },
      polishedSections: { ...polishedSections },
      date: new Date().toLocaleDateString()
    };
    setSaved(prev => [item, ...prev]);
    localStorage.setItem('ca_deliverables', JSON.stringify([item, ...saved]));
  };

  const handleExport = (format: 'md' | 'html') => {
    if (!selectedTemplate) return;
    const content = format === 'md' ? exportAsMarkdown(selectedTemplate, values) : exportAsHTML(selectedTemplate, values);
    const blob = new Blob([content], { type: format === 'md' ? 'text/markdown' : 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${values.PROJECT_NAME || 'deliverable'}.${format}`;
    a.click();
  };

  const currentSection = selectedTemplate?.sections[activeSection];
  const displayContent = currentSection ? (polishedSections[currentSection.title] || values[sectionKey(currentSection.title)] || currentSection.content) : '';

  return (
    <div className="min-h-screen bg-[#050b14] text-[#f0f0f0]">
      <header className="border-b border-white/10 bg-[#0a1628] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#d4af37] flex items-center justify-center text-[#0a1628] font-bold text-lg">C</div>
            <div>
              <h1 className="text-lg font-semibold tracking-tight text-[#f0f0f0]">CatalystAgile <span className="text-[#d4af37]">Consulting Deliverable Builder</span></h1>
              <p className="text-xs text-[#8a9bb8]">Template Engine + Optional AI Polish</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {selectedTemplate && (
              <>
                <button onClick={() => setPreviewMode(p => p === 'edit' ? 'preview' : 'edit')} className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 transition-colors">
                  {previewMode === 'edit' ? 'Preview' : 'Edit'}
                </button>
                <button onClick={() => handleExport('md')} className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 flex items-center gap-1.5 transition-colors">
                  <FileText className="w-3.5 h-3.5"/> Markdown
                </button>
                <button onClick={() => handleExport('html')} className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 flex items-center gap-1.5 transition-colors">
                  <FileCode className="w-3.5 h-3.5"/> HTML
                </button>
                <button onClick={handleSave} className="px-3 py-1.5 text-xs rounded-md bg-[#d4af37] text-[#0a1628] font-semibold hover:bg-[#e8c547] transition-colors flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5"/> Save
                </button>
              </>
            )}
            <SettingsButton className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 inline-flex items-center gap-1.5 transition-colors text-[#8a9bb8] hover:text-[#d4af37]" />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT: Template Gallery */}
        <aside className="lg:col-span-3 space-y-3">
          <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
            <h2 className="text-sm font-semibold text-[#d4af37] mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4"/> Templates</h2>
            <div className="space-y-2">
              {TEMPLATES.map(t => (
                <button key={t.id} onClick={() => handleTemplateSelect(t)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-xs transition-colors ${selectedTemplate?.id === t.id ? 'bg-[#d4af37]/20 border border-[#d4af37]/40' : 'bg-[#132238] border border-white/5 hover:bg-[#1a2d4a]'}`}>
                  <div className="font-medium text-[#f0f0f0] text-sm">{t.name}</div>
                  <div className="text-[10px] text-[#8a9bb8] mt-0.5">{t.description}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
            <h2 className="text-sm font-semibold text-[#d4af37] mb-3 flex items-center gap-2"><FileCheck className="w-4 h-4"/> Saved ({saved.length})</h2>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {saved.map(s => (
                <div key={s.id} className="bg-[#132238] rounded-lg p-2.5 border border-white/5">
                  <div className="text-xs font-medium text-[#f0f0f0]">{s.name}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] text-[#8a9bb8]">{s.date}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#0a1628] text-[#8a9bb8]">{TEMPLATES.find(t => t.id === s.template)?.name || s.template}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* CENTER: Editor */}
        <section className="lg:col-span-9 space-y-4">
          {selectedTemplate ? (
            <>
              {/* Raw Input & Auto-Map */}
              <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-[#f0f0f0]">Raw Content Input</h2>
                  <button onClick={handleAutoMap} className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-[#d4af37]/30 text-[#d4af37] transition-colors flex items-center gap-1.5">
                    <Wand2 className="w-3.5 h-3.5"/> Auto-Map to Sections
                  </button>
                </div>
                <textarea value={rawContent} onChange={e => setRawContent(e.target.value)}
                  placeholder="Paste rough notes, AI output, or bullet points here. Click Auto-Map to distribute into sections."
                  className="w-full h-32 bg-[#070f1a] border border-white/10 rounded-lg p-3 text-sm text-[#f0f0f0] placeholder-[#8a9bb8]/50 focus:outline-none focus:border-[#d4af37]/50 resize-none font-mono"/>
              </div>

              {/* Section Navigation */}
              <div className="flex gap-2 overflow-x-auto pb-2">
                {selectedTemplate.sections.map((s, i) => (
                  <button key={i} onClick={() => setActiveSection(i)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${activeSection === i ? 'bg-[#d4af37]/20 text-[#d4af37] border border-[#d4af37]/40' : 'bg-[#132238] text-[#8a9bb8] border border-white/5 hover:bg-[#1a2d4a]'}`}>
                    {polishedSections[s.title] && <Sparkles className="w-3 h-3 text-[#d4af37]"/>}
                    {i + 1}. {s.title}
                  </button>
                ))}
              </div>

              {/* Section Editor */}
              {currentSection && (
                <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-[#d4af37]">{currentSection.title}</h3>
                    <div className="flex items-center gap-2">
                      <button onClick={() => handlePolish(activeSection)} disabled={isPolishing === currentSection.title}
                        className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-[#d4af37]/30 text-[#d4af37] transition-colors flex items-center gap-1.5 disabled:opacity-50">
                        <Sparkles className="w-3.5 h-3.5"/> {isPolishing === currentSection.title ? 'Polishing...' : 'AI Polish'}
                      </button>
                      <button onClick={() => copyToClipboard(displayContent)} className="p-1.5 rounded bg-[#132238] text-[#8a9bb8] hover:text-[#d4af37] transition-colors">
                        <Copy className="w-4 h-4"/>
                      </button>
                    </div>
                  </div>
                  <textarea
                    value={displayContent}
                    onChange={e => {
                      setValues(prev => ({ ...prev, [sectionKey(currentSection.title)]: e.target.value }));
                      setPolishedSections(prev => { const n = { ...prev }; delete n[currentSection.title]; return n; });
                    }}
                    className="w-full h-64 bg-[#070f1a] border border-white/10 rounded-lg p-3 text-sm text-[#f0f0f0] placeholder-[#8a9bb8]/50 focus:outline-none focus:border-[#d4af37]/50 resize-none font-mono leading-relaxed"/>
                  {polishedSections[currentSection.title] && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-[#d4af37]">
                      <Sparkles className="w-3.5 h-3.5"/> AI-polished version active. Edit above to revert to manual.
                    </div>
                  )}
                </div>
              )}

              {/* Quick Placeholders */}
              <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
                <h3 className="text-sm font-semibold text-[#f0f0f0] mb-3">Quick Placeholders</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {['PROJECT_NAME', 'CLIENT_NAME', 'CLIENT', 'DATE', 'INDUSTRY', 'AMOUNT', 'TIMELINE', 'CONSULTANT'].map(key => (
                    <div key={key}>
                      <label className="text-[10px] uppercase tracking-wider text-[#8a9bb8] mb-1 block">{key}</label>
                      <input value={values[key] || ''} onChange={e => setValues(prev => ({ ...prev, [key]: e.target.value }))}
                        className="w-full bg-[#070f1a] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#f0f0f0] focus:outline-none focus:border-[#d4af37]/50"/>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-12 text-center">
              <FileSpreadsheet className="w-12 h-12 text-[#8a9bb8] mx-auto mb-4"/>
              <h2 className="text-lg font-semibold text-[#f0f0f0] mb-2">Select a Template</h2>
              <p className="text-sm text-[#8a9bb8]">Choose a deliverable template from the sidebar to get started.</p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function sectionKey(title: string) { return title.toUpperCase().replace(/\s+/g, '_'); }

function copyToClipboard(text: string) { navigator.clipboard.writeText(text); }
